/**
 * The alert list's table — shared by the local and the cloud list.
 *
 * Purely presentational: it takes the page of rows and the callbacks for the five lifecycle
 * actions, and knows nothing about where the rows came from. The two dashboards fetch through
 * completely different transports (local service vs. the node proxy) but an operator should
 * not be able to tell which one they're looking at.
 *
 * The shape it's built around is a QUEUE being worked top-down, not a spreadsheet being read:
 *
 *  - Severity is a stripe down the row's left edge, not a column. Urgency is the thing you
 *    scan for, and a colour at a fixed x-position is scannable in a way a pill that moves with
 *    the text beside it is not.
 *  - Alert name, camera and summary collapse into ONE cell, two lines. They're read together
 *    ("what happened, where"), and as separate columns they forced the row wide enough that
 *    Actions fell off the right edge on a laptop.
 *  - The row leads with the alert's own SNAPSHOT, thumbnail-sized. Half of triage is "is this
 *    the thing I'm looking for", and a picture answers that in a glance where a name and a
 *    camera take a sentence — a queue of "Person Detected · Main Gate" rows is otherwise
 *    indistinguishable from itself. Hosts that can't reach the images pass neither resolver
 *    and get the list exactly as it was.
 *  - The clock reading sits above the date, so same-day rows differ in the part you read first.
 *  - Terminal rows (resolved/cancelled) fade and drop to a single Reopen control — they're
 *    history, and keeping four live buttons on them invited misclicks on finished work.
 */
import type { ReactNode } from 'react';
import {
  CheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserPlusIcon,
  ArrowUturnLeftIcon,
  VideoCameraIcon,
  BellSlashIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { AlertStatusTag, SeverityTag, alertSeverityVar } from './AlertTags';
import { Avatar } from './Avatar';
import { alertName, alertWhen } from './alertVocab';
import { useAlertImage } from './useAlertImage';

const TH = 'px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap';

export interface AlertListTableProps {
  alerts: any[];
  loading?: boolean;
  /** camera id → display name, for the row's second line. */
  cameraMap?: Record<string, string>;
  /** user id → display name, for the assignee cell. */
  userMap?: Record<string, string>;
  /** Withheld without `alerts.edit`; the backend enforces it too. */
  canManage?: boolean;
  /**
   * The row's picture, as a URL the browser can load directly (a node, whose `image_urls`
   * point at storage the browser can reach). Pass `alertImageUrl` unless the transport needs
   * something cleverer. Omit BOTH this and `loadThumbnail` and the column disappears
   * entirely — no empty frames down a list that was never going to have pictures.
   */
  thumbnailFor?: (alert: any) => string | null;
  /**
   * Lazy resolver for hosts whose images aren't directly loadable — the cloud streams each
   * one through its tunnel proxy as a blob. Returns an object/blob URL or null; the table
   * owns revoking what it is handed. Tried only where `thumbnailFor` came back empty, and
   * never blocks the row from rendering.
   */
  loadThumbnail?: (alert: any) => Promise<string | null>;
  /** Row click — the index, so the caller can open its record and page through the list. */
  onOpen: (index: number) => void;
  onAcknowledge?: (alert: any) => void;
  onAssign?: (alert: any) => void;
  onResolve?: (alert: any) => void;
  onCancel?: (alert: any) => void;
  onReopen?: (alert: any) => void;
  /** Line under the empty state — e.g. "Try widening the time range." */
  emptyHint?: ReactNode;
}

function isTerminal(a: any) {
  return a?.status === 'resolved' || a?.status === 'cancelled';
}

/**
 * The row's snapshot: 16:9, the width of a thumbnail and no more.
 *
 * Small ON PURPOSE. What it has to answer is "is this the thing I'm looking for" while the
 * eye runs down a queue — a face, a truck, an empty yard — and at that job 80px does as well
 * as 400px while costing the row no extra height beyond its own two lines. The record behind
 * the row is where an alert gets looked AT.
 *
 * A row whose image is missing, still resolving, or broken keeps the SAME box with a muted
 * placeholder in it. An absent frame would let the text column shift left on some rows and
 * not others, which turns a scannable left edge into a ragged one — the exact thing the
 * severity stripe beside it exists to provide.
 */
function AlertThumb({
  alert,
  thumbnailFor,
  loadThumbnail,
}: {
  alert: any;
  thumbnailFor?: (alert: any) => string | null;
  loadThumbnail?: (alert: any) => Promise<string | null>;
}) {
  const { src, markFailed, ref } = useAlertImage(alert, thumbnailFor, loadThumbnail);

  return (
    <span ref={ref} className="block w-20 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
      <span className="relative block aspect-video w-full">
        {src ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
            onError={markFailed}
          />
        ) : (
          <PhotoIcon className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 text-muted-foreground opacity-40" />
        )}
      </span>
    </span>
  );
}

function ActionBtn({ title, onClick, className = '', children }: any) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={`flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${className}`}
    >
      {children}
    </button>
  );
}

export default function AlertListTable({
  alerts,
  loading,
  cameraMap = {},
  userMap = {},
  canManage = false,
  thumbnailFor,
  loadThumbnail,
  onOpen,
  onAcknowledge,
  onAssign,
  onResolve,
  onCancel,
  onReopen,
  emptyHint,
}: AlertListTableProps) {
  /** Whether this host can produce pictures at all. A list that can't shows no column for
   *  them rather than a page of grey rectangles. */
  const thumbs = !!(thumbnailFor || loadThumbnail);

  if (loading) {
    return (
      <div className="space-y-2 p-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (!alerts.length) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
        <BellSlashIcon className="h-8 w-8 text-muted-foreground opacity-40" />
        <p className="text-sm font-medium text-foreground">No alerts match these filters</p>
        {emptyHint && <p className="text-[13px] text-muted-foreground">{emptyHint}</p>}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      {/* `table-fixed` + a colgroup, not auto layout: the widths then come from the header
          rather than from whichever alert on THIS page happens to have the longest summary,
          so paging through the queue doesn't shuffle the columns sideways under the cursor —
          and it's what makes `truncate` in the alert cell actually truncate.

          PERCENTAGES, summing to 100, not rem. With fixed widths every column but the first
          is as narrow as its content, so all the slack on a wide screen lands in the alert
          column — which took over half the table and pushed everything else into a huddle on
          the right. Shares spread that slack across all five, and hold the same proportions
          from the min-width up to an ultrawide monitor. */}
      <table className="w-full min-w-[56rem] table-fixed text-[13px]">
        <colgroup>
          {/* Alert takes the extra when the thumbnail is on — the picture is 80px of the cell,
              and without the share the summary beside it truncates a word in. */}
          <col className={thumbs ? 'w-[38%]' : 'w-[34%]'} />
          <col className="w-[12%]" />
          <col className={thumbs ? 'w-[14%]' : 'w-[16%]'} />
          <col className={thumbs ? 'w-[18%]' : 'w-[20%]'} />
          <col className="w-[18%]" />
        </colgroup>
        <thead>
          <tr className="border-b border-border bg-muted/40">
            <th className={`${TH} pl-4`}>Alert</th>
            <th className={TH}>When</th>
            <th className={TH}>Status</th>
            <th className={TH}>Assignee</th>
            <th className={`${TH} pr-4 text-right`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {alerts.map((a, idx) => {
            const terminal = isTerminal(a);
            const when = alertWhen(a.event_timestamp);
            const camera = cameraMap[a.camera_id] || (a.camera_id ? String(a.camera_id).slice(0, 8) : '');
            const detail = a.summary || '';
            const assignee = a.assigned_to ? userMap[a.assigned_to] || 'Assigned' : '';
            const name = alertName(a);

            return (
              <tr
                key={a.id}
                onClick={() => onOpen(idx)}
                // Rows are opened with the mouse and with the keyboard: the list is worked
                // one-handed as often as not.
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(idx); }
                }}
                className={`group cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/40 focus:bg-muted/40 focus:outline-none ${terminal ? 'opacity-60' : ''}`}
              >
                {/* The severity stripe rides on the first cell as an inset shadow rather than
                    an absolutely-positioned span: it fills the cell's full height under
                    `border-collapse` without needing the row to become a positioning context. */}
                <td
                  className="py-2.5 pl-4 pr-3"
                  style={{ boxShadow: `inset 3px 0 0 ${alertSeverityVar(a.severity)}` }}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    {thumbs && (
                      <AlertThumb alert={a} thumbnailFor={thumbnailFor} loadThumbnail={loadThumbnail} />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-semibold text-foreground" title={name}>{name}</span>
                        <SeverityTag severity={a.severity} variant="soft" dot={false} className="shrink-0" />
                      </div>
                      <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[12px] text-muted-foreground">
                        <VideoCameraIcon className="h-3.5 w-3.5 shrink-0" />
                        <span className="max-w-[12rem] shrink-0 truncate font-medium text-foreground/80" title={camera}>
                          {camera || '—'}
                        </span>
                        {detail && (
                          <>
                            <span className="shrink-0 opacity-50">·</span>
                            <span className="truncate" title={detail}>{detail}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </td>

                <td className="whitespace-nowrap px-3 py-2.5 align-middle" title={when.full}>
                  <div className="font-medium text-foreground">{when.time}</div>
                  <div className="text-[12px] text-muted-foreground">{when.date}</div>
                </td>

                <td className="whitespace-nowrap px-3 py-2.5 align-middle">
                  <AlertStatusTag status={a.status} />
                </td>

                <td className="whitespace-nowrap px-3 py-2.5 align-middle">
                  {assignee ? (
                    <span className="flex items-center gap-2">
                      <Avatar name={assignee} className="h-7 w-7 text-[10px]" />
                      <span className="max-w-[9rem] truncate text-foreground">{assignee}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Unassigned</span>
                  )}
                </td>

                {/* Actions never open the record — the row does. */}
                <td className="px-3 py-2.5 pr-4 align-middle" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-0.5">
                    {!canManage ? (
                      <span className="text-[11px] text-muted-foreground">—</span>
                    ) : terminal ? (
                      <ActionBtn title="Reopen alert" onClick={() => onReopen?.(a)}>
                        <ArrowUturnLeftIcon className="h-4 w-4" />
                      </ActionBtn>
                    ) : (
                      <>
                        {a.status === 'new' && (
                          <ActionBtn title="Acknowledge" onClick={() => onAcknowledge?.(a)}>
                            <CheckIcon className="h-4 w-4" />
                          </ActionBtn>
                        )}
                        <ActionBtn title="Assign…" onClick={() => onAssign?.(a)}>
                          <UserPlusIcon className="h-4 w-4" />
                        </ActionBtn>
                        <ActionBtn title="Resolve" className="hover:text-emerald-500" onClick={() => onResolve?.(a)}>
                          <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                        </ActionBtn>
                        <ActionBtn title="Cancel (false positive)" className="hover:text-red-500" onClick={() => onCancel?.(a)}>
                          <XCircleIcon className="h-4 w-4 text-red-500" />
                        </ActionBtn>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
